import {
    AppBar,
    Toolbar,
    Typography,
    Container,
    IconButton,
    Drawer,
    useMediaQuery,
    ThemeProvider,
} from '@mui/material';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import MenuIcon from '@mui/icons-material/Menu';
import { darkTheme } from '../../theme/darkTheme';
import { UserApi } from '../../services/api';
import { useDrawer } from '../context/DrawerContext';
import { buildNavItems } from './components/navbar/navItems';
import NavDrawer from './components/navbar/NavDrawer';
import DesktopNav from './components/navbar/DesktopNav';

const NavBar = () => {
    const title = process.env.REACT_APP_TITLE;
    const version = process.env.REACT_APP_VERSION;
    const navigate = useNavigate();
    const { drawerOpen, setDrawerOpen } = useDrawer();
    const { isLoggedIn, logout, nutzer } = useAuth();
    const isMobile = useMediaQuery(darkTheme.breakpoints.down('md'));
    const [isAdmin, setIsAdmin] = useState(false);

    useEffect(() => {
        const checkAdminStatus = async () => {
            if (nutzer && nutzer.id) {
                try {
                    const response = await UserApi.getAdminStatus(nutzer.id);
                    setIsAdmin(response.data.isAdmin);
                } catch (error) {
                    if (error.status !== 401) {
                        console.error("Fehler beim Abrufen des Admin-Status:", error);
                    }
                }
            }
        };
        checkAdminStatus();
    }, [nutzer]);

    const handleNav = (path) => {
        navigate(path);
        setDrawerOpen(false);
    };

    const handleNavClick = (item) => {
        setDrawerOpen(false);
        navigate(item.path);
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
        setDrawerOpen(false);
    };

    const navItems = buildNavItems(isLoggedIn, isAdmin);

    return (
        <ThemeProvider theme={darkTheme}>
            <AppBar position="sticky" elevation={0} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                <Container maxWidth="lg">
                    <Toolbar disableGutters sx={{
                        minHeight: { xs: 56, md: 64 }, display: "flex", justifyContent: isMobile ? 'space-between' : 'center',
                    }}>
                        {/* Logo */}
                        <Typography
                            variant="h6"
                            sx={{
                                flexGrow: 1,
                                fontWeight: 700,
                                fontSize: { xs: '1.2rem', md: '1.5rem' },
                                cursor: 'pointer',
                                background: 'linear-gradient(45deg, #3b82f6 30%, #8b5cf6 90%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                            }}
                            onClick={() => handleNav('/dashboard')}
                        >
                            {title}
                        </Typography>

                        {/* Desktop Navigation */}
                        {!isMobile && (
                            <DesktopNav
                                navItems={navItems}
                                isLoggedIn={isLoggedIn}
                                nutzer={nutzer}
                                onNavClick={handleNavClick}
                                onNav={handleNav}
                                onLogout={handleLogout}
                            />
                        )}

                        {isMobile && (
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: { xs: '1.2rem', md: '1.5rem' },
                                    cursor: 'pointer',
                                    background: 'linear-gradient(45deg, #3b82f6 30%, #8b5cf6 90%)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                }}
                                onClick={() => handleNav('/dashboard')}
                            >
                                {version}
                            </Typography>
                        )}

                        {/* Mobile: Hamburger */}
                        {isMobile && (
                            <IconButton
                                onClick={() => setDrawerOpen(true)}
                                color="inherit"
                                sx={{ ml: 1 }}
                            >
                                <MenuIcon />
                            </IconButton>
                        )}
                    </Toolbar>
                </Container>
            </AppBar>

            {/* Mobile Drawer */}
            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                PaperProps={{
                    sx: { bgcolor: 'background.default' },
                }}
            >
                <NavDrawer
                    title={title}
                    isLoggedIn={isLoggedIn}
                    nutzer={nutzer}
                    navItems={navItems}
                    onNav={handleNav}
                    onNavClick={handleNavClick}
                    onLogout={handleLogout}
                    onClose={() => setDrawerOpen(false)}
                />
            </Drawer>
        </ThemeProvider>
    );
};

export default NavBar;
